import * as React from "react";

import { AdBand } from "@/components/shared/main/ad-band";
import { AdSlot } from "./ad-slot";
import { NativeAdSlot } from "./native-ad-slot";

/**
 * Brand-neutral ad positions that have NO legacy creative in the revamp. They
 * carry only the slot contract and collapse to zero height until a creative
 * serves (`.hkej-ad-band-optional`), so adding one changes nothing on screen.
 * `?adpreview=1` draws them at size for design review.
 *
 * Brand chrome is the shared `AdBand` (every brand already uses it); pass
 * `label` for the English brands.
 */

/** Right Hand Banner (300×100) — directly under the first rail unit. */
export function RightBannerSlot({ label, sticky = false, unit = "RightBanner" }: { label?: string; sticky?: boolean; unit?: string }) {
  return (
    <AdBand label={label} className={`hkej-ad-band-optional${sticky ? " lg:sticky hkej-sticky-offset" : ""}`}>
      <AdSlot unit={unit} className="w-full" />
    </AdBand>
  );
}

/** In-read outstream unit inside an article body (1×1 / 6×6 / fluid). */
export function InReadSlot({ label }: { label?: string }) {
  return (
    <div className="hkej-ad-band-optional">
      <AdBand label={label}>
        <AdSlot unit="InRead" className="w-full" />
      </AdBand>
    </div>
  );
}

export { InterscrollerSlot } from "./interscroller-slot";
export { OutstreamSlot } from "./outstream-ad";

/** In-content banner (BigBanner) with a per-brand size list; e.g. EJ Monthly 600×250, EJ Health 820×160. */
export function BigBannerSlot({
  label,
  sizes,
  unit = "BigBanner",
  className = "",
}: {
  label?: string;
  sizes?: { desktop?: string[]; tablet?: string[]; mobile?: string[] };
  unit?: string;
  className?: string;
}) {
  return (
    <div className={`hkej-ad-band-optional ${className}`}>
      <AdBand label={label}>
        <AdSlot unit={unit} sizes={sizes} className="w-full" />
      </AdBand>
    </div>
  );
}

/**
 * Native list row (InBanner). Wrap the editorial row that already sits in the
 * sponsored position; it is the fallback until a native creative serves.
 * With no children it is an invisible, zero-height position.
 */
export function NativeRowSlot({
  children,
  className = "",
  requestOverflow = false,
}: {
  children?: React.ReactNode;
  className?: string;
  /** Past InBanner's count, still request GAM. An empty answer shows `children`. */
  requestOverflow?: boolean;
}) {
  return (
    <NativeAdSlot unit="InBanner" className={className} requestOverflow={requestOverflow}>
      {children}
    </NativeAdSlot>
  );
}

/** Native grid card (NativeAd). */
export function NativeCardSlot({
  children,
  className = "",
  showOverflow = false,
  frameCss,
}: {
  children?: React.ReactNode;
  className?: string;
  showOverflow?: boolean;
  frameCss?: string;
}) {
  return (
    <NativeAdSlot unit="NativeAd" className={className} showOverflow={showOverflow} frameCss={frameCss}>
      {children}
    </NativeAdSlot>
  );
}

/**
 * Native list rows the client way: every `every`-th editorial row BECOMES the native
 * position (InBanner) with that row as its fallback — blank at the row's height while
 * GAM answers, the creative when one serves, the editorial row when nothing does. Nothing
 * is inserted, so the list never has an empty slot or an extra item. At most `max` rows.
 */
export function nativeRows(nodes: React.ReactNode[], every: number, max = 3, className = ""): React.ReactNode[] {
  let k = 0;
  return nodes.map((n, i) =>
    (i + 1) % every === 0 && k < max ? (
      <NativeRowSlot key={`native-${k++}`} className={className}>
        {n}
      </NativeRowSlot>
    ) : (
      n
    ),
  );
}

/** Sponsor-logo sizes (EJ Health SponsorLogo units); fluid lets a native text creative size itself. */
const SPONSOR_LOGO_SIZES = {
  desktop: ["300x70", "300x50", "260x90", "160x50", "fluid"],
  tablet: ["300x70", "300x50", "fluid"],
  mobile: ["300x70", "300x50", "fluid"],
};

/**
 * A sponsor logo / text link beside a section header or byline (SponsorBanner): a
 * display unit, so it carries the 廣告 label and a fixed 70px box like every other band.
 */
export function SponsorBannerSlot({
  unit = "SponsorBanner",
  label,
  sizes = SPONSOR_LOGO_SIZES,
  className = "",
}: {
  unit?: string;
  label?: string;
  sizes?: { desktop?: string[]; tablet?: string[]; mobile?: string[] };
  className?: string;
}) {
  return (
    <div className={`hkej-ad-band-optional ${className}`}>
      <AdBand label={label}>
        <AdSlot unit={unit} sizes={sizes} className="w-full" />
      </AdBand>
    </div>
  );
}

/**
 * Insert a slot after every `every`-th node (at most `max` slots). Used for the
 * "every 3rd row" native positions on brand lists and for in-article banners.
 * `inject` renders extra nodes after given indices (the phone in-article LREC);
 * a periodic slot that would land right under an injected node waits for the
 * next node instead, so two units never stack.
 */
export function interleaveSlots(nodes: React.ReactNode[], every: number, render: (k: number) => React.ReactNode, max = 3, inject?: Record<number, React.ReactNode>): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let k = 0;
  let due = false;
  nodes.forEach((n, i) => {
    out.push(n);
    const injected = inject?.[i];
    if (injected !== undefined) out.push(<React.Fragment key={`inject-${i}`}>{injected}</React.Fragment>);
    if ((i + 1) % every === 0 && k < max) due = true;
    if (due && injected === undefined) { out.push(<React.Fragment key={`ad-${k}`}>{render(k++)}</React.Fragment>); due = false; }
  });
  return out;
}

/* ───────── in-article LREC: the legacy mobile rule ─────────
 * The old HKEJ / EJ Health article templates (views/landing/article.php) put the phone
 * 300×250 after paragraph 1, or after paragraph 2 when paragraph 1 is shorter than 40
 * characters (mb_strlen, so characters not bytes) — never above real content. */

export interface ArticleNodeMeta {
  kind: "p" | "other";
  text?: string;
}

/** Index of the node the in-article LREC follows; -1 when the body has no paragraph. */
export function mobileLrecIndex(meta: ArticleNodeMeta[], minChars = 40): number {
  const paragraphs = meta.map((m, i) => (m.kind === "p" ? i : -1)).filter((i) => i >= 0);
  if (!paragraphs.length) return -1;
  const first = paragraphs[0];
  const len = [...(meta[first].text ?? "").trim()].length;
  if (len >= minChars) return first;
  return paragraphs[1] ?? first;
}

/**
 * The in-article LREC for the narrow layouts, where the rail has dropped under the
 * story. Band-gated by EMPTY size lists (never by CSS alone: the provider counts every
 * slot element for auto-numbering, but only requests bands that have sizes), so in DOM
 * order it is `LargeRectangle1` and the rail units follow. `tablet={false}` limits it
 * to phones (HKEJ: tablets keep the 728×90 in-content banner after paragraph 1).
 */
export function MobileArticleLrec({
  label,
  unit = "LargeRectangle",
  tablet = true,
  className = "",
}: {
  label?: string;
  unit?: string;
  tablet?: boolean;
  className?: string;
}) {
  const sizes = tablet ? { desktop: [] } : { desktop: [], tablet: [] };
  return (
    <div className={`hkej-ad-band-optional ${tablet ? "lg:hidden" : "md:hidden"} ${className}`} data-ad-inarticle="">
      <AdBand label={label}>
        <AdSlot unit={unit} sizes={sizes} className="w-full" />
      </AdBand>
    </div>
  );
}

/** `ArticleNodeMeta` for a list of nodes with a `kind`/`type` discriminator and text. */
export function articleMeta<T>(nodes: readonly T[], isParagraph: (n: T) => boolean, textOf: (n: T) => string | undefined = (n) => (n as { text?: string }).text): ArticleNodeMeta[] {
  return nodes.map((n) => ({ kind: isParagraph(n) ? "p" : "other", text: textOf(n) }));
}

/**
 * Sizes for a search 精選 row (SponsorBanner1–5).
 *
 * 5×1 / 53×22 / 165×22 are legacy text sizes, and only SponsorBanner1 and 2 still
 * carry them. SponsorBanner3–5 and the House line item carry the logo sizes, and
 * each creative serves once per page view, so every band offers the sizes those
 * units and the line item share (220×30 first, so the chip reserves 30px).
 */
export const SEARCH_SPONSOR_SIZES = {
  desktop: ["220x30", "300x30", "300x50", "320x40", "300x70", "160x50", "620x60", "260x90"],
  tablet: ["220x30", "300x30", "300x50", "320x40", "300x70", "160x50", "620x60", "260x90"],
  mobile: ["220x30", "300x30", "300x50", "320x40", "300x70", "160x50", "620x60", "260x90"],
};

/**
 * A row of text-ad positions (SponsorBanner): the 精選 terms under a search bar,
 * the home subscriber text links. `terms` are the fallback chips (null = empty
 * position that collapses until a creative serves).
 */
export function TextAdRow({
  terms,
  onPick,
  prefix,
  className = "",
  chipClassName = "",
  chipStyle,
  sizes = SEARCH_SPONSOR_SIZES,
  startIndex,
  label = "廣告",
}: {
  sizes?: { desktop?: string[]; tablet?: string[]; mobile?: string[] };
  terms: Array<string | null>;
  onPick?: (term: string) => void;
  prefix?: React.ReactNode;
  className?: string;
  chipClassName?: string;
  chipStyle?: React.CSSProperties;
  /** Explicit unit numbers `SponsorBanner{startIndex+i}` (two rows on one page must not collide). */
  startIndex?: number;
  /** The row's own 廣告 caption (a display unit must carry one); `null` for the nav text links. */
  label?: string | null;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`} data-ad-textrow="">
      {label ? (
        <span aria-hidden="true" style={{ fontSize: 11, letterSpacing: "0.12em", color: "var(--hkej-ad-label, #767676)" }}>
          {label}
        </span>
      ) : null}
      {prefix}
      {terms.map((term, i) => (
        <AdSlot key={i} unit={startIndex !== undefined ? `SponsorBanner${startIndex + i}` : "SponsorBanner"} sizes={sizes} className="inline-flex">
          {term ? (
            <button type="button" onClick={() => onPick?.(term)} className={`inline-flex items-center hover:opacity-80 ${chipClassName}`} style={chipStyle}>
              {term}
            </button>
          ) : null}
        </AdSlot>
      ))}
    </div>
  );
}
